// frontend/src/components/GPSDeviceDialog.js
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Navigation, X, Copy, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedInput } from './themed';
import Modal from './Modal';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';
import gpsDeviceService from '../services/gpsDeviceService';

const GPSDeviceDialog = ({ isOpen, onClose, device, entityId, vehicles, onSaved }) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();

  // Form state
  const [formData, setFormData] = useState({
    device_name: '',
    device_type: '',
    device_identifier: '',
    vehicle_id: '',
    is_default: false,
    priority: 0,
    status: 'active'
  });

  const [loading, setLoading] = useState(false);
  const [rotatingApiKey, setRotatingApiKey] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiKeyDevice, setApiKeyDevice] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  // Initialize form with device data if editing
  useEffect(() => {
    if (device) {
      setFormData({
        device_name: device.device_name || '',
        device_type: device.device_type || '',
        device_identifier: device.device_identifier || '',
        vehicle_id: device.vehicle_id || '',
        is_default: device.is_default || false,
        priority: device.priority || 0,
        status: device.status || 'active'
      });
      setApiKeyDevice(null);
      setShowApiKey(false);
      setShowRotateConfirm(false);
      setRotatingApiKey(false);
    } else {
      // Reset form for new device
      setFormData({
        device_name: '',
        device_type: '',
        device_identifier: '',
        vehicle_id: '',
        is_default: false,
        priority: 0,
        status: 'active'
      });
      setApiKeyDevice(null);
      setShowApiKey(false);
      setShowRotateConfirm(false);
      setRotatingApiKey(false);
    }
    setErrors({});
  }, [device, isOpen]);

  const resetApiKeyReveal = () => {
    setApiKeyDevice(null);
    setShowApiKey(false);
    setShowRotateConfirm(false);
    setRotatingApiKey(false);
  };

  const handleDialogClose = () => {
    if (showApiKey) {
      return;
    }

    resetApiKeyReveal();
    onClose();
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.device_name.trim()) {
      newErrors.device_name = t('common.required', { defaultValue: 'Required' });
    }

    if (!formData.device_identifier.trim()) {
      newErrors.device_identifier = t('common.required', { defaultValue: 'Required' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data
      const deviceData = {
        device_name: formData.device_name.trim(),
        device_type: formData.device_type || null,
        device_identifier: formData.device_identifier.trim(),
        vehicle_id: formData.vehicle_id ? parseInt(formData.vehicle_id) : null,
        is_default: formData.is_default,
        priority: parseInt(formData.priority) || 0,
        status: formData.status
      };

      if (device) {
        // Update existing device
        await gpsDeviceService.updateDevice(entityId, device.id, deviceData);
        toast.success(t('gpsDevices.deviceUpdated'));
        onSaved();
      } else {
        // Create new device
        const createdDeviceData = await gpsDeviceService.createDevice(entityId, deviceData);
        setApiKeyDevice(createdDeviceData);
        setShowApiKey(true);
        toast.success(t('gpsDevices.deviceCreated'));
        // Don't close dialog yet - show API key first
      }
    } catch (error) {
      console.error('Error saving GPS device:', error);

      // Handle duplicate identifier error
      if (error.response?.data?.error?.includes('duplicate') ||
          error.response?.data?.error?.includes('already exists')) {
        toast.error(t('gpsDevices.errors.duplicateIdentifier'));
        setErrors({ device_identifier: t('gpsDevices.errors.duplicateIdentifier') });
      } else {
        toast.error(device ?
          t('gpsDevices.errors.updateFailed') :
          t('gpsDevices.errors.createFailed')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle copy API key
  const handleCopyApiKey = () => {
    if (apiKeyDevice?.api_key) {
      navigator.clipboard.writeText(apiKeyDevice.api_key);
      toast.success(t('gpsDevices.apiKeyCopied'));
    }
  };

  const handleRotateApiKey = async () => {
    if (!device?.id) {
      return;
    }

    setRotatingApiKey(true);

    try {
      const rotatedDevice = await gpsDeviceService.rotateApiKey(entityId, device.id);
      setShowRotateConfirm(false);
      // Keep the plaintext key out of form state and parent device lists. It is
      // held only long enough for the admin to copy this one-time response.
      setApiKeyDevice(rotatedDevice);
      setShowApiKey(true);
      toast.success(t('gpsDevices.apiKeyRegenerated', {
        defaultValue: 'GPS device API key regenerated successfully'
      }));
    } catch (error) {
      setShowRotateConfirm(false);
      console.error('Error regenerating GPS device API key', {
        error: error.message,
        entityId,
        deviceId: device.id
      });
      toast.error(t('gpsDevices.apiKeyRegenerateFailed', {
        defaultValue: 'Failed to regenerate GPS device API key'
      }));
    } finally {
      setRotatingApiKey(false);
    }
  };

  // Handle close after one-time API key shown
  const handleAcknowledgeApiKey = () => {
    setApiKeyDevice(null);
    setShowApiKey(false);
    onSaved();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDialogClose}
      closeOnBackdropClick={!showApiKey}
      showCloseButton={!showApiKey}
    >
      <div className={cn('p-6', background.card)}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Navigation className="w-6 h-6 text-blue-500" />
            <h2 className={cn('text-xl font-bold', text.primary)}>
              {device ? t('gpsDevices.editDevice') : t('gpsDevices.addDevice')}
            </h2>
          </div>
          {!showApiKey && (
            <button
              onClick={handleDialogClose}
              className={cn('p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors')}
              aria-label={t('common.close', { defaultValue: 'Close' })}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* One-time API Key Display */}
        {showApiKey && apiKeyDevice && (
          <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                  {t('gpsDevices.apiKeyWarning')}
                </h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  {t('common.copyAndSave', { defaultValue: 'Copy this API key now. It will not be shown again.' })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded border border-yellow-300 dark:border-yellow-700">
              <code className={cn('flex-1 font-mono text-sm break-all', text.primary)}>
                {apiKeyDevice.api_key}
              </code>
              <button
                onClick={handleCopyApiKey}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors shrink-0"
                title={t('gpsDevices.copyApiKey')}
              >
                <Copy className="w-4 h-4 text-blue-500" />
              </button>
            </div>

            <div className="mt-4 flex justify-end">
              <ThemedButton onClick={handleAcknowledgeApiKey}>
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('common.done', { defaultValue: 'Done' })}
              </ThemedButton>
            </div>
          </div>
        )}

        {/* Form */}
        {!showApiKey && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Device Name */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', text.primary)}>
                {t('gpsDevices.deviceName')} <span className="text-red-500">*</span>
              </label>
              <ThemedInput
                type="text"
                name="device_name"
                value={formData.device_name}
                onChange={handleChange}
                placeholder={t('common.enterName', { defaultValue: 'Enter device name' })}
                className={errors.device_name ? 'border-red-500' : ''}
              />
              {errors.device_name && (
                <p className="mt-1 text-sm text-red-500">{errors.device_name}</p>
              )}
            </div>

            {/* Device Identifier */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', text.primary)}>
                {t('gpsDevices.deviceIdentifier')} <span className="text-red-500">*</span>
              </label>
              <ThemedInput
                type="text"
                name="device_identifier"
                value={formData.device_identifier}
                onChange={handleChange}
                placeholder={t('common.enterIdentifier', { defaultValue: 'IMEI or Serial Number' })}
                className={errors.device_identifier ? 'border-red-500' : ''}
              />
              {errors.device_identifier && (
                <p className="mt-1 text-sm text-red-500">{errors.device_identifier}</p>
              )}
            </div>

            {/* Device Type */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', text.primary)}>
                {t('gpsDevices.deviceType')}
              </label>
              <select
                name="device_type"
                value={formData.device_type}
                onChange={handleChange}
                className={cn(
                  'w-full px-4 py-2 rounded-lg border',
                  background.input,
                  text.primary,
                  'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                )}
              >
                <option value="">{t('common.select', { defaultValue: 'select' })}</option>
                <option value="cellular_gps">{t('gpsDevices.typeOptions.cellular_gps')}</option>
                <option value="obd2">{t('gpsDevices.typeOptions.obd2')}</option>
                <option value="hardwired">{t('gpsDevices.typeOptions.hardwired')}</option>
                <option value="other">{t('gpsDevices.typeOptions.other')}</option>
              </select>
            </div>

            {/* Assigned Vehicle */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', text.primary)}>
                {t('gpsDevices.assignedVehicle')}
              </label>
              <select
                name="vehicle_id"
                value={formData.vehicle_id}
                onChange={handleChange}
                className={cn(
                  'w-full px-4 py-2 rounded-lg border',
                  background.input,
                  text.primary,
                  'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                )}
              >
                <option value="">{t('common.unassigned', { defaultValue: 'Unassigned' })}</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.vehicle_number}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className={cn('block text-sm font-medium mb-1', text.primary)}>
                {t('gpsDevices.status')}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={cn(
                  'w-full px-4 py-2 rounded-lg border',
                  background.input,
                  text.primary,
                  'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                )}
              >
                <option value="active">{t('gpsDevices.statusOptions.active')}</option>
                <option value="inactive">{t('gpsDevices.statusOptions.inactive')}</option>
                <option value="maintenance">{t('gpsDevices.statusOptions.maintenance')}</option>
              </select>
            </div>

            {/* Default Device Checkbox */}
            {formData.vehicle_id && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  name="is_default"
                  checked={formData.is_default}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_default" className={cn('text-sm font-medium', text.primary)}>
                  {t('gpsDevices.isDefault')}
                </label>
              </div>
            )}

            {/* API Key Rotation */}
            {device && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-2">
                    <div>
                      <p className={cn('text-sm font-medium', text.primary)}>
                        {t('gpsDevices.apiKey')}
                      </p>
                      <p className={cn('text-sm', text.secondary)}>
                        {device.api_key_configured
                          ? t('gpsDevices.apiKeyConfigured', { defaultValue: 'API key configured' })
                          : t('gpsDevices.apiKeyNotConfigured', { defaultValue: 'API key not configured' })}
                      </p>
                    </div>
                    {device.api_key_preview && (
                      <code className={cn('inline-block rounded border border-gray-200 dark:border-gray-700 px-2 py-1 font-mono text-sm', text.primary)}>
                        {device.api_key_preview}
                      </code>
                    )}
                  </div>
                  <ThemedButton
                    type="button"
                    variant="secondary"
                    onClick={() => setShowRotateConfirm(true)}
                    disabled={loading || rotatingApiKey}
                    icon={RefreshCw}
                  >
                    {t('gpsDevices.regenerateApiKey', { defaultValue: 'Regenerate API Key' })}
                  </ThemedButton>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <ThemedButton
                type="button"
                onClick={handleDialogClose}
                variant="secondary"
                disabled={loading}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </ThemedButton>
              <ThemedButton
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('common.saving', { defaultValue: 'Saving...' })}
                  </span>
                ) : (
                  device ? t('common.update', { defaultValue: 'Update' }) : t('common.create', { defaultValue: 'Create' })
                )}
              </ThemedButton>
            </div>
          </form>
        )}

        <ConfirmDialogShell
          isOpen={showRotateConfirm}
          onClose={() => !rotatingApiKey && setShowRotateConfirm(false)}
          onConfirm={handleRotateApiKey}
          title={t('gpsDevices.confirmRegenerateApiKeyTitle', { defaultValue: 'Regenerate API key?' })}
          confirmLabel={t('gpsDevices.confirmRegenerateApiKeyAction', { defaultValue: 'Regenerate API Key' })}
          confirmVariant="warning"
          confirmIcon={RefreshCw}
          isLoading={rotatingApiKey}
          isDisabled={rotatingApiKey}
          showIcon={true}
          iconVariant="warning"
          closeOnBackdropClick={!rotatingApiKey}
        >
          <p className={cn('text-sm', text.secondary)}>
            {t('gpsDevices.confirmRegenerateApiKeyMessage', {
              defaultValue: 'The current device key will stop working immediately. You will need to update the device with the new key.'
            })}
          </p>
        </ConfirmDialogShell>
      </div>
    </Modal>
  );
};

export default GPSDeviceDialog;
